import { usePortalMessage } from "../../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `channels` — unit P11-04.
 *
 * The concurrency summary, the channel rows, the Connection definition list with rotate, the
 * signalling table, the "when it cannot connect" states, and (in `answering-panel.tsx`) when
 * it answers and what happens when every line is busy.
 *
 * THREE PLACES THE DESIGN'S COPY HAD TO CHANGE, ALL FOR THE SAME REASON — the carrier cannot
 * do what the drawing claims, and a console that repeats the claim turns a known limit into
 * a support conversation during an incident:
 *
 *   1. `sip:roma-pizzeria@sip.wetalk.io` → `sip:<slug>@sip.telnyx.com`, with a line saying
 *      whose domain that is. Telnyx's vanity SIP domains exist only on IP, FQDN and
 *      Teams-auth connections; the **credential** connections a per-VoiceAgent user-part
 *      requires cannot carry one, and the domain is the carrier's in any case (A7). The API
 *      returns `sip_address_owner` precisely so this screen cannot get it wrong.
 *   2. `Status: Registered · 4 ms ago` → the registration is rendered only when one has been
 *      **observed**, carries a `title` saying so when WeTalk synthesised it, and reads "Not
 *      observed" otherwise. A carrier that exposes no REGISTER leaves a gap; a green badge over
 *      a gap is a fabricated fact.
 *   3. There is **no inbound failover badge.** `inbound_failover` arrives as a refusal with a
 *      reason and a named alternative, and the Number row's `title` says a number is homed on
 *      one carrier and that dual-homing your own trunk is the redundancy that exists (A8).
 *
 * AMENDED 2026-09-23 (fidelity polish): the design's rows and nothing more. The notes that
 * qualify a figure — whose domain the address is on, the failover refusal, "measured by
 * WeTalk", the username, codecs and key-store name — are the `title` of the figure they qualify
 * rather than paragraphs and extra rows the design does not draw.
 *
 * `Change` and `Request quota` lead to Settings and Billing, where the concurrency limit and
 * the tier are actually changed. `Change provider` has no flow yet and is drawn disabled.
 *
 * The channel list is this agent's bindings, followed by every messaging medium it is NOT
 * bound to — so WhatsApp and Viber read "Coming soon" and the web-chat widget "Not connected",
 * each where the design puts it, instead of silently missing.
 *
 * AMENDED 2026-09-25 (customer-portal review #94, #115, fix round package HI):
 *
 *   - Status comes from what WeTalk OBSERVED. A carrier number never REGISTERs, so "Not observed"
 *     beside a number that has taken calls was a true statement about the wrong thing: a number's
 *     status is its calls ("Taking calls · last call 2 hours ago"); a registration is shown only
 *     for a SIP trunk, which is the only thing that registers.
 *   - SIP is a trunk's business. With no SIP credential there is no "No SIP credential" /
 *     "No registration has been observed" warning and no "Rotate SIP password" button — a carrier
 *     number is not missing a credential it never needed.
 *   - The Events row is drawn only when the API hands a URL (it hands `null` until hooks.wetalk.io
 *     is delegated, RC-73).
 *   - "Change" (which led to the generic Settings page) and "Request quota" (which led to a Billing
 *     page that could not act) are one "Request more lines" button that records a request the
 *     WeTalk team acts on (`QuotaRequestDialog`).
 *
 * AMENDED 2026-09-30 (plan-008 ST-UA, design §22.14, R-UI-7): the account's own SIP trunk. Under the
 * channel rows, each trunk number connected to this agent reads **Your SIP trunk** with the trunk's
 * observed status, whether it receives conversations (or why not) and whether the agent calls out
 * from it; **Connect a trunk number** binds one of the account's unbound trunk numbers, or — with no
 * trunk yet — opens the trunk dialog Numbers & SIP uses (`sip-trunk-binding.tsx`). A trunk number is
 * named "A number on your own SIP trunk" in the voice row's detail, and a carrier-only agent still
 * gets no SIP warning (review 115).
 *
 * AMENDED 2026-10-09 (design sync, the Claude Design export of 2026-10-09). The tab is the design's
 * Settings · Channels:
 *
 *   1. **Calls at once** — this agent's lines, "Change", and what the organization allows across all agents, with
 *      "Request more lines".
 *   2. **Channels** — the Phone card ("Calling number" for a survey): Working / Requested / Not connected
 *      from `usePhoneConnection`, the number in large figures, one line under it, and "Test call" /
 *      "Call me" (the Test tab) or "Connect" (the connect dialog); the Web chat card with "Add to your
 *      website"; "Coming soon: WhatsApp, Viber".
 *   3. **Advanced: phone connection**, collapsed (`advanced-phone-connection.tsx`): the trunk's settings,
 *      sign-in details, recent activity and Disconnect — or, for a number WeTalk issued, the connection
 *      facts and signalling trace drawn here before; with nothing connected, the trunk numbers no agent
 *      answers yet.
 *   4. **When it answers** — for an agent that answers calls only (`answering-panel.tsx`).
 *
 * AMENDED 2026-10-09 (plan-015 LIM, gap G11, ADR 0045, owner answer 4). "Calls at once · Change" is enabled for
 * whoever holds `voice_agent.write` (Owner, Admin, Member, Developer): it opens the design's Capacity control — the
 * wizard's `3 · 5 · 10 · 25 · 50` segments, those above the organization's limit drawn unavailable ("Above your
 * organization's limit"), "Your organization allows N at once across all agents · Request more lines" — and saves
 * through `PUT /v1/agents/{voice_agent_id}/concurrency`. No new version is made; new calls meet the new figure within
 * seconds and a call in progress is never cut off. A choice equal to the published version's own ceiling is saved as
 * "the version's own" (`null`), so the agent keeps following its version. Above the entitlement the API refuses
 * (`422 channel_unit_limit_above_entitlement`) and the dialog says so with "Request more lines".
 *
 * AMENDED 2026-10-28 (plan-029 WAPP-PROT; design §40.13.1): under the handoff and pass panels, for an agent that answers
 * calls, the three caller-protection panels — **Caller protection** with **Blocked numbers**
 * (`caller-protection-panel.tsx`), **Who may call this agent** (`access-panel.tsx`, WAPP-ACCESS) and, in Release 2,
 * **Confirm who's calling** (`confirmation-panel.tsx`, WAPP-CONF). Each draws itself; this tab only mounts them. A survey
 * only makes calls, and caller protection covers inbound legs only (D-3), so it shows none of them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Dialog, Icon, Table, TableBody, TableCell, TableRow, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "./channels.css";
// The Capacity control's segments are the wizard's (`c-setup-seg`, `c-setup-seg-over`), as the agent page's edit
// panel already draws them.
import "../../new/new-agent.css";

import { AnsweringPanel } from "./answering-panel.js";
import { HandoffPanel } from "./handoff-panel.js";
import { VoiceAgentPassPanel } from "./voice-agent-pass-panel.js";
import { CallerProtectionPanel } from "./caller-protection-panel.js";
import { AccessPanel } from "./access-panel.js";
import { ConfirmationPanel } from "./confirmation-panel.js";
import { AdvancedPhoneConnection } from "./advanced-phone-connection.js";
import { usePhoneConnection, type PhoneConnectionView } from "./phone-connection.js";
import { SipTrunkBindingPanel, inboundWords } from "./sip-trunk-binding.js";
import { displayNumberOf } from "../../../numbers/number-display.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { PendingQuotaRequestLine, QuotaRequestDialog } from "../../../billing/quota-request-dialog.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useGateway } from "../../../../api/provider.js";
import { apiErrorText } from "../../../../api/api-error.js";
import {
  ChannelUnitLimitAboveEntitlementError,
  MASKED_PASSWORD,
  REGISTRATION_NOT_OBSERVED,
  type ChannelUnitLimitView,
} from "../../../../api/channel-view.js";
import { ConnectionRates } from "./connection-rates.js";
import { channelUnitChoiceOf } from "../../new/wizard-rules.js";
import {
  QUERY_KEY,
  answeringQuery,
  channelBindingQuery,
  concurrencyQuery,
  connectionQuery,
  messagingMediumListQuery,
  signallingQuery,
  voiceAgentQuery,
} from "../../../../api/query.js";
import { useOrganizationConcurrency } from "../../../finance/allowances/organization-allowance.js";
import type {
  VoiceAgentConnectionView,
  ConcurrencySummaryView,
  Medium,
  SignallingEventView,
} from "../../../../api/gateway.js";

export interface ChannelBindingTabProps {
  readonly voiceAgentId: string;
  /** A survey: it makes calls, so the phone is its calling number and there are no opening hours. */
  readonly outbound: boolean;
  /** "Test call" / "Call me": the Test tab. */
  readonly onTestCall: () => void;
  /** "Connect": the connect dialog (the Agent page owns it, as its banner opens it too). */
  readonly onConnect: () => void;
  /** "See the N callers": History. */
  readonly onSeeCallers: () => void;
  /** "Open v9 on Versions" (the pass panel's waiting draft, plan-021 review M1): Versions with that version selected. */
  readonly onOpenAgentVersion: (agentVersionId: string) => void;
}

export function ChannelBindingTab({
  voiceAgentId,
  outbound,
  onTestCall,
  onConnect,
  onSeeCallers,
  onOpenAgentVersion,
}: ChannelBindingTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const binding = useQuery(channelBindingQuery(gateway, voiceAgentId));
  const concurrency = useQuery(concurrencyQuery(gateway, voiceAgentId));
  const connection = useQuery(connectionQuery(gateway, voiceAgentId));
  const answering = useQuery(answeringQuery(gateway, voiceAgentId));
  const organizationConcurrency = useOrganizationConcurrency();
  const signalling = useQuery(signallingQuery(gateway, voiceAgentId));
  const medium = useQuery(messagingMediumListQuery(gateway));
  const phone = usePhoneConnection(voiceAgentId, outbound);
  const voiceAgent = useQuery(voiceAgentQuery(gateway, voiceAgentId));

  // A failed read is said, never left behind the loading line: the tab waited for ever on a
  // trunk number whose `carrier` the reader refused (2026-10-02).
  const failed = [binding, concurrency, connection].find((one) => one.isError);
  if (failed !== undefined) {
    return (
      <p className="c-note c-note-critical" role="alert">
        {portalText("operations", "we_could_not_read_how_this_agent_is_reachable_2f4bb678")}{" "}
        {apiErrorText(failed.error, portalText("operations", "reload_the_page_to_try_again_d6649089"))}
      </p>
    );
  }
  if (
    binding.data === undefined ||
    concurrency.data === undefined ||
    connection.data === undefined ||
    phone === undefined
  ) {
    return <p className="c-note">{portalText("operations", "reading_how_this_agent_is_reachable_10cd5d0f")}</p>;
  }

  const webChat = binding.data.filter((one) => one.medium === "web_chat");
  const soon = (medium.data ?? []).filter((one) => one.state === "coming_soon" || one.availability === "waitlisted");

  return (
    <>
      <ConcurrencySummary
        voiceAgentId={voiceAgentId}
        summary={concurrency.data}
        outbound={outbound}
        webChatOn={webChat.some((one) => one.enabled)}
      />
      <TroubleList summary={concurrency.data} connection={connection.data} />

      <p className="ch-section-title ch-section-title-flush">{portalText("operations", "channels_18e03e2a")}</p>
      <ul className="ch-list">
        <PhoneRow
          phone={phone}
          lastConversationText={connection.data.voiceObserved?.lastConversationAtText ?? null}
          onTestCall={onTestCall}
          onConnect={onConnect}
        />
        <li className="ch-row">
          <span className="ch-mark">
            <Icon glyph={GLYPH.chatText} size="xl" />
          </span>
          <span className="ch-row-body">
            <span className="ch-row-name">
              <span>{portalText("operations", "web_chat_109d8596")}</span>
              {webChat.some((one) => one.enabled) ? (
                <Tag status="positive">{portalText("operations", "on_your_site_913a8559")}</Tag>
              ) : (
                <Tag tone="neutral">{portalText("operations", "not_on_your_site_yet_6475360f")}</Tag>
              )}
            </span>
            <span className="ch-row-meta">{portalText("operations", "copy_one_line_of_code_to_your_site_73050ebe")}</span>
          </span>
          <Link
            to="/messaging/$mediumCode"
            params={{ mediumCode: "web_chat" }}
            // glossary-lint-disable-next-line agent -- the public ?agent= query parameter name (FR-WC-19)
            search={{ agent: voiceAgentId }}
            className={webChat.length > 0 ? "btn btn-secondary" : "btn btn-primary"}
          >
            {webChat.length > 0 ? portalText("operations", "get_snippet_daf347f9") : portalText("operations", "add_to_your_website_e6df0ea2")}
          </Link>
        </li>
        {soon.length === 0 ? null : (
          <li className="ch-soon">{portalText("operations", "coming_soon_b185259a")}{soon.map((one) => MEDIUM_SHORT_NAME[one.medium]).join(", ")}</li>
        )}
      </ul>

      {/* plan-017 WAPP-B (design §29.11 row 12): the rates of its connections. */}
      <ConnectionRates voiceAgentId={voiceAgentId} />

      <AdvancedPhoneConnection
        voiceAgentId={voiceAgentId}
        phone={phone}
        carrierDetail={
          <>
            {phone.e164 === null ? null : (
              <div className="c-va-split">
                <ConnectionPanel voiceAgentId={voiceAgentId} connection={connection.data} />
                <div>
                  <p className="c-va-label">{portalText("operations", "recent_signalling_8ed75511")}</p>
                  {signalling.data === undefined ? (
                    <p className="c-note">{portalText("operations", "reading_the_signalling_trace_2d4e612e")}</p>
                  ) : (
                    <SignallingTable event={signalling.data} />
                  )}
                </div>
              </div>
            )}
            {/* The account's own trunk numbers no agent answers yet, to connect one here. */}
            <SipTrunkBindingPanel voiceAgentId={voiceAgentId} />
          </>
        }
      />

      {outbound ? null : answering.data === undefined ? (
        <p className="c-note">{portalText("operations", "reading_when_it_answers_c4d80a50")}</p>
      ) : (
        <AnsweringPanel
          voiceAgentId={voiceAgentId}
          schedule={answering.data}
          currentCeiling={ceilingToRaise(concurrency.data, organizationConcurrency)}
          voiceAgentCeiling={concurrency.data.channelUnitCeiling}
          onSeeCallers={onSeeCallers}
        />
      )}

      {/* plan-018 WAPP-C: putting a caller through to a person, beside "Forward to a phone" (hidden when not offered). */}
      {outbound ? null : <HandoffPanel voiceAgentId={voiceAgentId} languageCode={voiceAgent.data?.languageCode ?? []} />}
      <VoiceAgentPassPanel
        voiceAgentId={voiceAgentId}
        languageCode={voiceAgent.data?.languageCode ?? []}
        agentTemplateCode={voiceAgent.data?.agentTemplateCode ?? null}
        onOpenAgentVersion={onOpenAgentVersion}
      />

      {/* plan-029 (design §40.13.1): caller protection, who may call, and (Release 2) confirming who is calling. */}
      {outbound ? null : (
        <>
          <CallerProtectionPanel voiceAgentId={voiceAgentId} />
          <AccessPanel voiceAgentId={voiceAgentId} agentTemplateCode={voiceAgent.data?.agentTemplateCode ?? null} />
          <ConfirmationPanel voiceAgentId={voiceAgentId} agentTemplateCode={voiceAgent.data?.agentTemplateCode ?? null} />
        </>
      )}
    </>
  );
}

/** The design's short names for the media that are not here yet. */
const MEDIUM_SHORT_NAME = {
  get voice() { return portalText("operations", "voice_3091c844"); },
  get web_chat() { return portalText("operations", "web_chat_109d8596"); },
  whatsapp: "WhatsApp",
  get viber() { return portalText("operations", "viber_70774829"); },
} as const satisfies Record<Medium, string>;

/** The Phone card: the design's `pc.title`, `pc.state`, `pc.number`, `pc.note` and its one act. */
function PhoneRow({
  phone,
  lastConversationText,
  onTestCall,
  onConnect,
}: {
  readonly phone: PhoneConnectionView;
  readonly lastConversationText: string | null;
  readonly onTestCall: () => void;
  readonly onConnect: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const canManage = useMemberCan().can("sip_trunk.manage");
  const note =
    phone.state === "requested"
      ? portalText("operations", "requested_we_re_setting_it_up_it_appears_here_once_it_is_c_c7c528b4")
      : phone.state === "none"
        ? phone.outbound
          ? (phone.gap !== null && phone.trunk.length > 0 ? phone.gap : portalText("operations", "connect_your_provider_so_campaigns_can_start_calling_c6de79b2"))
          : portalText("operations", "connect_your_number_so_customers_reach_the_agent_04c350ae")
        : !phone.outbound && phone.trunkRow !== null && !phone.trunkRow.trunk.inboundReadiness.ready
          ? inboundWords(phone.trunkRow.trunk)
          : phone.outbound
          ? portalText("operations", "the_number_people_see_when_the_agent_calls_d0e7c7c1")
          : lastConversationText === null
            ? portalText("operations", "the_number_your_customers_call_cdb6e76a")
            : portalText("operations", "the_number_your_customers_call_last_call_value_e4adb27f", { v0: String(lastConversationText) });
  return (
    <li className="ch-row ch-row-phone">
      <span className="ch-mark">
        <Icon glyph={GLYPH.phoneCall} size="xl" />
      </span>
      <span className="ch-row-body">
        <span className="ch-row-name">
          <span>{phone.outbound ? portalText("operations", "calling_number_2cdf6a37") : portalText("operations", "phone_77064d52")}</span>
          {phone.state === "working" ? (
            <Tag status="positive">{portalText("operations", "working_3b4dfc97")}</Tag>
          ) : (
            <Tag tone="neutral">{phone.state === "requested" ? portalText("operations", "requested_we_re_setting_it_up_39e02eca") : portalText("operations", "not_connected_8b02f3de")}</Tag>
          )}
        </span>
        <span className="ch-phone-number">
          {phone.e164 !== null
            ? displayNumberOf(phone.e164)
            : phone.state === "requested"
              ? portalText("operations", "we_are_setting_it_up_24ab6129")
              : portalText("operations", "no_number_yet_ff1aed4b")}
        </span>
        <span className="ch-row-meta">{note}</span>
      </span>
      {phone.state === "working" ? (
        <Button variant="secondary" onClick={onTestCall}>
          <Icon glyph={GLYPH.phoneOutgoing} size="md" />
          <span>{phone.outbound ? portalText("operations", "call_me_a9ba81ad") : portalText("operations", "test_call_ab98c76c")}</span>
        </Button>
      ) : phone.state === "none" && canManage ? (
        <Button variant="primary" onClick={onConnect}>
          {portalText("operations", "connect_b65463cb")}</Button>
      ) : null}
    </li>
  );
}

/**
 * The limit a request would raise: the organization's concurrency entitlement (INT 2026-10-07 — a granted request raises
 * the organization's, not a tier's), else — while it is being read — the agent's own.
 */
function ceilingToRaise(summary: ConcurrencySummaryView, organizationConcurrency: number | null): number | null {
  return organizationConcurrency ?? summary.channelUnitCeiling;
}

function ConcurrencySummary({
  voiceAgentId,
  summary,
  outbound,
  webChatOn,
}: {
  readonly voiceAgentId: string;
  readonly summary: ConcurrencySummaryView;
  readonly outbound: boolean;
  readonly webChatOn: boolean;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const member = useMemberCan();
  const ceiling = summary.channelUnitCeiling;
  const organizationConcurrency = useOrganizationConcurrency();
  const [quotaRequestOpen, setQuotaRequestOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const limit = summary.channelUnitLimit;
  const setLimit = gateway.setVoiceAgentChannelUnitLimit;
  const changeRefusal = !member.can("voice_agent.write")
    ? (member.whyNot("voice_agent.write") ?? portalText("operations", "your_role_cannot_change_this_agent_f931de81"))
    : ceiling === null
      ? portalText("operations", "this_agent_has_no_published_version_yet_so_there_are_no_ca_adaad20b")
      : null;
  return (
    <div className="ch-concurrency">
      <span
        className="ch-concurrency-value"
        role="meter"
        aria-valuenow={summary.inUse}
        aria-valuemin={0}
        aria-valuemax={ceiling ?? 0}
        aria-valuetext={
          ceiling === null
            ? portalText("operations", "no_ceiling_yet_6b0f2d84")
            : portalText("operations", "value_of_value_in_use_1e7c4a92", { v0: String(summary.inUse), v1: String(ceiling) })
        }
        aria-label={portalText("operations", "lines_in_use_e4a8d799")}
        title={
          ceiling === null
            ? portalText("operations", "no_ceiling_yet_this_agent_has_no_published_version_which_i_b810a9af")
            : portalText("operations", "value_in_use_now_ad676251", { v0: String(String(summary.inUse)) })
        }
      >
        <strong>{portalText("operations", "calls_at_once_eb655228")}</strong> <span className="ch-concurrency-figure">{ceiling === null ? "—" : String(ceiling)}</span>
      </span>
      <Button
        variant="ghost"
        disabled={changeRefusal !== null}
        title={changeRefusal ?? undefined}
        onClick={() => {
          setChangeOpen(true);
        }}
      >
        {portalText("operations", "change_64fbd995")}</Button>
      <span className="ch-concurrency-note">
        {organizationConcurrency === null ? null : (
          <>{portalText("operations", "your_organization_allows_897e2dd0")}{String(organizationConcurrency)} {portalText("operations", "across_all_agents_60ab1e7a")}</>
        )}
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            setQuotaRequestOpen(true);
          }}
        >
          {portalText("operations", "request_more_lines_cba2054b")}</a>
        <PendingQuotaRequestLine />
      </span>
      {changeOpen && ceiling !== null ? (
        <ChannelUnitLimitDialog
          voiceAgentId={voiceAgentId}
          effectiveCeiling={ceiling}
          limit={limit}
          outbound={outbound}
          webChatOn={webChatOn}
          save={setLimit}
          onRequestMore={() => {
            setChangeOpen(false);
            setQuotaRequestOpen(true);
          }}
          onClose={() => {
            setChangeOpen(false);
          }}
        />
      ) : null}
      <QuotaRequestDialog
        open={quotaRequestOpen}
        currentCeiling={ceilingToRaise(summary, organizationConcurrency)}
        onClose={() => {
          setQuotaRequestOpen(false);
        }}
      />
    </div>
  );
}

/**
 * The choices the dialog draws: the wizard's `3 · 5 · 10 · 25 · 50` (with the organization's figure among them when it
 * is not one), plus the agent's figure today and the published version's own when either is none of them — so what is
 * in force is always the one checked, and the way back to the version's own is always there. Those above the
 * organization's entitlement are drawn, and unavailable.
 */
function limitChoiceOf(
  entitlementCeiling: number,
  current: number,
  versionCeiling: number | null,
): readonly { readonly count: number; readonly over: boolean }[] {
  const choice = [...channelUnitChoiceOf(entitlementCeiling)];
  for (const count of versionCeiling === null ? [current] : [current, versionCeiling]) {
    if (!choice.some((one) => one.count === count)) choice.push({ count, over: count > entitlementCeiling });
  }
  return choice.sort((one, other) => one.count - other.count);
}

/**
 * "Calls at once · Change" — the design's Capacity control in a dialog (plan-015 LIM, G11).
 *
 * Saving writes the agent's own limit; a choice equal to the published version's own ceiling is saved as `null`, so
 * the agent keeps following its version rather than pinning a copy of it. The summary the API answers replaces the
 * tab's, so "Calls at once" shows the new figure at once.
 */
function ChannelUnitLimitDialog({
  voiceAgentId,
  effectiveCeiling,
  limit,
  outbound,
  webChatOn,
  save,
  onRequestMore,
  onClose,
}: {
  readonly voiceAgentId: string;
  readonly effectiveCeiling: number;
  readonly limit: ChannelUnitLimitView;
  readonly outbound: boolean;
  readonly webChatOn: boolean;
  readonly save: (voiceAgentId: string, limit: number | null) => Promise<ConcurrencySummaryView>;
  readonly onRequestMore: () => void;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState(effectiveCeiling);
  const [problem, setProblem] = usePortalMessage();
  const saving = useMutation({
    mutationFn: (next: number | null) => save(voiceAgentId, next),
    onSuccess: async (summary) => {
      queryClient.setQueryData([...QUERY_KEY.concurrency, voiceAgentId], summary);
      await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.concurrency, voiceAgentId] });
      onClose();
    },
    onError: (error: unknown) => {
      setProblem(
        () => (error instanceof ChannelUnitLimitAboveEntitlementError
          ? apiErrorText(error, "")
          : apiErrorText(error, portalText("operations", "the_change_could_not_be_saved_nothing_has_changed_431f1c49"))),
      );
    },
  });
  const label = !outbound && webChatOn ? portalText("operations", "calls_and_chats_at_once_57bade7b") : portalText("operations", "calls_at_once_cb8a5b2e");
  const share = outbound
    ? portalText("operations", "the_agent_makes_up_to_value_calls_at_the_same_time_f7db569c", { v0: String(String(chosen)) })
    : webChatOn
      ? portalText("operations", "calls_and_chats_share_these_lines_b9f83b80")
      : "";
  return (
    <Dialog
      open
      title={portalText("operations", "capacity_45bd908d")}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {portalText("operations", "cancel_77dfd213")}</Button>
          <Button
            variant="primary"
            disabled={saving.isPending || chosen === effectiveCeiling}
            onClick={() => {
              setProblem(null);
              saving.mutate(chosen === limit.versionCeiling ? null : chosen);
            }}
          >
            {saving.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_efc007a3")}
          </Button>
        </>
      }
    >
      <div className="field">
        <span className="c-setup-label" id="channel-unit-limit-label">
          {label}
        </span>
        <div className="seg c-setup-seg" role="radiogroup" aria-labelledby="channel-unit-limit-label">
          {limitChoiceOf(limit.entitlementCeiling, effectiveCeiling, limit.versionCeiling).map((one) => (
            <label
              key={one.count}
              className={one.over ? "seg-opt c-setup-seg-over" : "seg-opt"}
              title={one.over ? portalText("operations", "above_your_organization_s_limit_5cf132fe") : undefined}
            >
              <input
                type="radio"
                name="channelUnitLimit"
                value={String(one.count)}
                checked={chosen === one.count}
                disabled={one.over}
                onChange={() => {
                  setChosen(one.count);
                }}
              />
              <span>{String(one.count)}</span>
            </label>
          ))}
        </div>
      </div>
      <span className="c-setup-hint">
        {share}{portalText("operations", "your_organization_allows_897e2dd0")}{String(limit.entitlementCeiling)} {portalText("operations", "at_once_across_all_agents_e6593c97")}{" "}
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onRequestMore();
          }}
        >
          {portalText("operations", "request_more_lines_cba2054b")}</a>
      </span>
      <span className="c-setup-hint">{portalText("operations", "new_calls_follow_the_new_figure_within_seconds_a_call_in_p_b7623030")}</span>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
    </Dialog>
  );
}

/**
 * "When it cannot connect", as a list of things that are actually wrong.
 *
 * Every item is derived from a fact the API returned, and an absent fact produces an item
 * that says it is absent rather than nothing at all. The list disappears entirely when there
 * is nothing to report, which is the only state that should look calm.
 */
function TroubleList({
  summary,
  connection,
}: {
  readonly summary: ConcurrencySummaryView;
  readonly connection: VoiceAgentConnectionView;
}): ReactNode {
  usePortalTranslation("operations");
  const trouble: { readonly key: string; readonly title: string; readonly dek: string; readonly critical: boolean }[] = [];

  if (summary.voiceAgentState !== "live") {
    trouble.push({
      key: "state",
      title: portalText("operations", "this_agent_is_value_42647beb", { v0: String(summary.voiceAgentState) }),
      dek:
        summary.voiceAgentState === "paused"
          ? portalText("operations", "a_paused_agent_does_not_answer_resume_it_from_the_header_e60d110b")
          : portalText("operations", "it_will_not_answer_until_it_is_live_26c5a246"),
      critical: summary.voiceAgentState === "failed",
    });
  }

  if (summary.channelUnitCeiling === null) {
    trouble.push({
      key: "ceiling",
      title: portalText("operations", "no_published_version_0e214920"),
      dek:
        portalText("operations", "the_concurrency_ceiling_the_opening_hours_and_the_overflow_3c0d07ac"),
      critical: true,
    });
  }

  // Nothing connected is the Phone card's "Not connected" and the page's banner (design sync
  // 2026-10-09), not a third warning here.

  // Review #94/#115: registration is a SIP trunk's fact. A carrier number has no credential to
  // miss and never registers, so neither absence is reported unless a trunk exists.
  if (connection.sip !== null && connection.registration === null) {
    trouble.push({
      key: "registration",
      title: portalText("operations", "no_registration_has_been_observed_50fb8aae"),
      dek:
        portalText("operations", "value_it_is_a_gap_in_what_the_carrier_exposes_not_a_fault_779d7840", { v0: String(REGISTRATION_NOT_OBSERVED()) }),
      critical: false,
    });
  } else if (
    connection.sip !== null &&
    connection.registration !== null &&
    connection.registration.responseCode !== null &&
    connection.registration.responseCode >= 400
  ) {
    trouble.push({
      key: "registration-failed",
      title: portalText("operations", "the_last_registration_answered_value_afcaa795", { v0: String(String(connection.registration.responseCode)) }),
      dek: portalText("operations", "observed_value_check_the_credentials_on_your_pbx_a613d663", { v0: String(connection.registration.observedAtText) }),
      critical: true,
    });
  }

  if (trouble.length === 0) return null;

  const critical = trouble.some((one) => one.critical);
  return (
    <ul className={critical ? "ch-trouble ch-trouble-critical" : "ch-trouble"} role="status">
      {trouble.map((one) => (
        <li className="ch-trouble-item" key={one.key}>
          <Icon glyph={GLYPH.warningCircle} size="xl" tone={one.critical ? "critical" : "caution"} />
          <span>
            <span className="ch-trouble-title">{one.title}</span>
            {one.dek}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** `+14155550142` → `+1 415 555 0142`. Other plans are shown as the carrier gave them. */
function formatE164(address: string): string {
  const nanp = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(address);
  return nanp === null ? address : `+1 ${nanp[1] ?? ""} ${nanp[2] ?? ""} ${nanp[3] ?? ""}`;
}

/**
 * Who provides the number. A customer surface never names a carrier (CLAUDE.md, the 2026-10-15 audit): a number
 * WeTalk issued is wetalk's; `null` is a number on the account's own SIP trunk, which no WeTalk carrier homes.
 */
function providerName(carrier: string | null): string {
  return carrier === null ? portalText("operations", "your_own_sip_trunk_4116a6ff") : portalText("operations", "provider_wetalk_8a1f5d26");
}

/**
 * The Connection definition list, with rotate.
 *
 * The password row shows a mask and nothing else: the console never receives a password to
 * mask, and `secret_ref` beside it is a key-store **name**. Rotation answers 202 — the worker
 * issues the new secret and writes it to the key store, so the current password keeps working
 * and no new value is ever shown here.
 */
function ConnectionPanel({
  voiceAgentId,
  connection,
}: {
  readonly voiceAgentId: string;
  readonly connection: VoiceAgentConnectionView;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [requested, setRequested] = useState(false);

  const rotate = useMutation({
    mutationFn: () => gateway.requestSipRotation(voiceAgentId),
    onSuccess: () => {
      setRequested(true);
      void queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.connection, voiceAgentId] });
    },
  });

  return (
    <div>
      <p className="c-va-label">{portalText("operations", "connection_6512ee15")}</p>
      <dl className="ch-facts">
        <dt className="ch-term">{portalText("operations", "number_b7baa1d4")}</dt>
        <dd
          className="ch-def ch-def-numeric"
          title={portalText("operations", "inbound_cannot_fail_over_between_carriers_value_16fac1c3", { v0: String(connection.inboundFailover.explanation) })}
        >
          {connection.number === null ? (
            portalText("operations", "none_bound_1d82defe")
          ) : (
            <>
              {formatE164(connection.number.e164)} ·{" "}
              {connection.number.source === "bring_your_own" ? portalText("operations", "yours_3c9d1e7a") : portalText("operations", "issued_by_wetalk_5476a348")}
            </>
          )}
        </dd>

        <dt className="ch-term">{portalText("operations", "provider_7ceee3f3")}</dt>
        <dd className="ch-def">
          {connection.number !== null
            ? providerName(connection.number.carrier)
            : (connection.sip?.connectionHost ?? "—")}
        </dd>

        <dt className="ch-term">{portalText("operations", "method_88306943")}</dt>
        <dd className="ch-def">
          {connection.sip !== null || connection.number?.bindingKind === "sip_trunk_number"
            ? portalText("operations", "sip_trunk_b97ab9f2")
            : connection.number !== null
              ? portalText("operations", "carrier_number_62abce45")
              : "—"}
        </dd>

        <dt className="ch-term">{portalText("operations", "status_bae7d5be")}</dt>
        <dd className="ch-def">
          {connection.sip === null ? (
            <ObservedStatus connection={connection} />
          ) : connection.registration === null ? (
            <span title={REGISTRATION_NOT_OBSERVED()}>
              <Tag status="muted">{portalText("operations", "not_observed_60ac6312")}</Tag>
            </span>
          ) : (
            <span
              title={
                connection.registration.synthesised
                  ? portalText("operations", "measured_by_wetalk_the_carrier_reports_no_registration_so_090351b2")
                  : undefined
              }
            >
              <Tag
                status={
                  connection.registration.responseCode !== null &&
                  connection.registration.responseCode >= 400
                    ? "critical"
                    : "positive"
                }
              >
                {connection.registration.responseCode === null
                  ? portalText("operations", "observed_2e159b44")
                  : connection.registration.responseCode < 400
                    ? portalText("operations", "registered_a844fcf8")
                    : String(connection.registration.responseCode)}{" "}
                · {connection.registration.observedAtText}
              </Tag>
            </span>
          )}
        </dd>

        {connection.sip === null ? null : (
          <>
            <dt className="ch-term">{portalText("operations", "inbound_6b6e7903")}</dt>
            <dd
              className="ch-def"
              title={
                portalText("operations", "valueusername_value_codecs_value_ddc26cea", { v0: String(connection.sip.sipAddressOwner === "carrier" ? `${CARRIER_DOMAIN_NOTE()} ` : ""), v1: String(connection.sip.username), v2: String(connection.sip.codec.join(", ")) })
              }
            >
              {connection.sip.sipAddress}
            </dd>
          </>
        )}

        <dt className="ch-term">{portalText("operations", "outbound_2c7807c0")}</dt>
        <dd className="ch-def">
          {connection.number === null
            ? portalText("operations", "no_caller_id_yet_29763378")
            : portalText("operations", "via_the_same_trunk_caller_id_value_170b96c0", { v0: String(formatE164(connection.number.e164)) })}
        </dd>

        {connection.eventsUrl === null ? null : (
          <>
            <dt className="ch-term">{portalText("operations", "events_c5497bca")}</dt>
            <dd className="ch-def">{connection.eventsUrl}</dd>
          </>
        )}
      </dl>

      <div className="ch-actions">
        {connection.sip === null ? null : (
          <Button
            variant="secondary"
            title={portalText("operations", "the_password_is_never_shown_it_is_value_here_and_lives_in_a5207a93", { v0: String(MASKED_PASSWORD), v1: String(connection.sip.secretRef) })}
            disabled={rotate.isPending || requested}
            onClick={() => {
              rotate.mutate();
            }}
          >
            {requested ? portalText("operations", "rotation_requested_1cdf498d") : portalText("operations", "rotate_sip_password_98ab54ef")}
          </Button>
        )}
        <Button variant="ghost" disabled>
          {portalText("operations", "change_provider_soon_8202f957")}</Button>
      </div>
      {requested ? (
        <p className="ch-note" role="status">
          {portalText("operations", "queued_the_new_password_is_issued_at_the_carrier_and_writt_ae4137ac")}</p>
      ) : null}
      {rotate.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {portalText("operations", "the_rotation_could_not_be_requested_nothing_has_changed_3f93f618")}</p>
      ) : null}
    </div>
  );
}

/**
 * A carrier number's status, from what WeTalk observed on the line (review #94): the last
 * conversation and the week's count. "Not observed" survives only for an API that does not send
 * the observation yet.
 */
function ObservedStatus({ connection }: { readonly connection: VoiceAgentConnectionView }): ReactNode {
  usePortalTranslation("operations");
  if (connection.number === null) return <>—</>;
  const observed = connection.voiceObserved;
  if (observed === null) {
    return (
      <span title={REGISTRATION_NOT_OBSERVED()}>
        <Tag status="muted">{portalText("operations", "not_observed_60ac6312")}</Tag>
      </span>
    );
  }
  if (observed.lastConversationAtText === null) {
    return (
      <span title={portalText("operations", "no_caller_has_reached_this_number_yet_place_a_test_call_fr_2e318b6f")}>
        <Tag status="muted">{portalText("operations", "no_calls_yet_dd2175b6")}</Tag>
      </span>
    );
  }
  return (
    <span
      title={portalText("operations", "value_value_in_the_last_7_days_test_calls_not_counted_e808a874", { v0: String(String(observed.conversationLast7Day)), v1: String(observed.conversationLast7Day === 1 ? portalText("operations", "call_bc8d8647") : portalText("operations", "calls_51b6cb25")) })}
    >
      <Tag status="positive">{portalText("operations", "taking_calls_last_call_ac2ef0e5")}{observed.lastConversationAtText}</Tag>
    </span>
  );
}

/** Whose domain the inbound address is on — the Inbound row's `title`. */
const CARRIER_DOMAIN_NOTE =
  () => (portalText("operations", "that_inbound_address_is_on_your_carrier_s_domain_not_wetal_1b3d5083"))

/** `200 OK` — the SIP response the way the design writes it, the code alone when unknown. */
const SIP_REASON = () => (new Map<number, string>([
  [100, portalText("operations", "trying_773fd0d1")],
  [180, portalText("operations", "ringing_81b62ad7")],
  [183, portalText("operations", "session_progress_e382c457")],
  [200, "OK"],
  [401, portalText("operations", "unauthorized_740b8315")],
  [403, portalText("operations", "forbidden_3dab5f60")],
  [404, portalText("operations", "not_found_d205cbd6")],
  [408, portalText("operations", "request_timeout_dca0c381")],
  [480, portalText("operations", "temporarily_unavailable_e9dc866e")],
  [486, portalText("operations", "busy_here_7b6903f1")],
  [487, portalText("operations", "request_terminated_251fd0e3")],
  [503, portalText("operations", "service_unavailable_ffbd0b77")],
]));

function responseText(code: number | null): string {
  if (code === null) return "—";
  const reason = SIP_REASON().get(code);
  return reason === undefined ? String(code) : `${String(code)} ${reason}`;
}

function SignallingTable({ event }: { readonly event: readonly SignallingEventView[] }): ReactNode {
  usePortalTranslation("operations");
  if (event.length === 0) {
    return (
      <p className="c-note">
        {portalText("operations", "nothing_yet_sip_facts_appear_here_as_conversations_are_set_1995cf8e")}</p>
    );
  }
  return (
    <div className="ch-signal">
    <Table caption={portalText("operations", "recent_sip_signalling_for_this_agent_62a39e14")}>
      <TableBody>
        {event.map((one) => (
          <TableRow key={one.signallingEventId}>
            <TableCell>
              <span className="ch-signal-time">{one.atText}</span>
            </TableCell>
            <TableCell>
              <span title={one.synthesised ? portalText("operations", "measured_by_wetalk_rather_than_reported_by_the_carrier_896be0e6") : undefined}>
                {one.kind}
                {one.peer === null ? null : <> {portalText("operations", "from_585af9b2")}{one.peer}</>}
                {one.detail === null ? null : <> · {one.detail}</>}
              </span>
            </TableCell>
            <TableCell numeric>{responseText(one.responseCode)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </div>
  );
}
